import { Injectable } from '@angular/core'
import { ActivatedRouteSnapshot, NavigationEnd, NavigationStart, Router } from '@angular/router'
import { IonRouterOutlet, NavController } from '@ionic/angular'
import { Store } from '@ngxs/store'
import { BehaviorSubject, Observable, distinctUntilChanged, filter, firstValueFrom, fromEvent, interval, map, merge, of, race, shareReplay, skipWhile, timer } from 'rxjs'
import { IsoDate, todayIso } from '../domain/dates'
import { NavTarget, screenPath } from '../domain/links'
import { NavMeta, SCREENS, ScreenKey, TipKey, tipFor, titleFor } from '../domain/screens'
import { AppState } from '../state-man/state/app.state'
import { OfflineService } from './offline.service'
import { UiService } from './ui.service'

export interface ChromeState {
  /** null → no Back button (Schedule). Label = previous stack entry's title, else the route's `up` title. */
  readonly back: { readonly label: string } | null
  readonly screen: ScreenKey | null          // drawer highlight
  readonly tip: TipKey | null
  readonly showNewWor: boolean               // false on WOR routes (meta.hideNewWor)
}

export interface ToastSpec { readonly title: string; readonly sub?: string; readonly sticky?: boolean; readonly tone?: 'ok' | 'error' }

/** Implemented by BasePage; read off the shell's active outlet component, never registered. */
export interface RefreshAware {
  hasUnsavedChanges?(): boolean
  onRefresh?(): void
}

interface NavEntry { readonly stateId: number; readonly url: string; readonly title: string; readonly meta: NavMeta }
interface PendingNav { readonly popstate: boolean; readonly restoredId: number | undefined; readonly replace: boolean }

const NO_CHROME: ChromeState = { back: null, screen: null, tip: null, showNewWor: false }
const TOAST_MS = 3500
const REFRESH_CAP_MS = 15000
// A fast local refresh would otherwise flash the box; the mock-up's standard spinner time.
const REFRESH_MIN_MS = 550

const deepest = (s: ActivatedRouteSnapshot): ActivatedRouteSnapshot => (s.firstChild ? deepest(s.firstChild) : s)

/**
 * The app chrome's one interface. Pages use go / back / toast / busyWhile / today$; the header, drawer,
 * rt-page, rt-tip, rt-toast and rt-loading read the rest. The back stack has one writer: Router events.
 */
@Injectable({ providedIn: 'root' })
export class ShellService {
  private stack: readonly NavEntry[] = []
  private pending: PendingNav | null = null
  private outlet: IonRouterOutlet | null = null
  private toastTimer: ReturnType<typeof setTimeout> | null = null
  private busyCount = 0

  private readonly chromeState = new BehaviorSubject<ChromeState>(NO_CHROME)
  private readonly toastState = new BehaviorSubject<ToastSpec | null>(null)
  private readonly busyState = new BehaviorSubject<{ readonly message: string | null } | null>(null)
  private readonly drawerState = new BehaviorSubject(false)
  private readonly tipState = new BehaviorSubject(false)

  readonly chrome$ = this.chromeState.asObservable()
  readonly toast$ = this.toastState.asObservable()
  readonly busy$ = this.busyState.asObservable()
  readonly drawerOpen$ = this.drawerState.pipe(distinctUntilChanged())
  readonly tipOpen$ = this.tipState.asObservable()
  /** Ticks over at midnight and when the app comes back to the foreground. */
  readonly today$: Observable<IsoDate> = merge(of(0), interval(60_000), fromEvent(document, 'visibilitychange')).pipe(
    map(todayIso), distinctUntilChanged(), shareReplay({ bufferSize: 1, refCount: false }))
  readonly refreshing$: Observable<boolean> = this.store.select(AppState.getApp).pipe(map(app => !!app?.is_refreshing), distinctUntilChanged())

  constructor(
    private router: Router,
    private nav: NavController,
    private store: Store,
    private ui: UiService,
    private offline: OfflineService,
  ) {
    router.events.subscribe(e => {
      if (e instanceof NavigationStart) {
        this.pending = {
          popstate: e.navigationTrigger === 'popstate',
          restoredId: e.restoredState?.navigationId,
          replace: !!router.getCurrentNavigation()?.extras.replaceUrl,
        }
      } else if (e instanceof NavigationEnd) {
        this.sync(e)
      }
    })
  }

  private sync(e: NavigationEnd) {
    const snap = deepest(this.router.routerState.snapshot.root)
    const meta = snap.data['nav'] as NavMeta | undefined
    const pending = this.pending ?? { popstate: false, restoredId: undefined, replace: false }
    this.pending = null
    if (!meta) {
      this.stack = []
      this.chromeState.next(NO_CHROME)
      return
    }
    const entry: NavEntry = { stateId: pending.restoredId ?? e.id, url: e.urlAfterRedirects, title: titleFor(meta, snap), meta }
    if (pending.popstate) {
      const i = this.stack.findIndex(x => x.stateId === entry.stateId)
      this.stack = i >= 0 ? this.stack.slice(0, i + 1) : [entry]
    } else if (pending.replace && this.stack.length > 0) {
      this.stack = [...this.stack.slice(0, -1), entry]
    } else if (meta.kind === 'screen') {
      this.stack = [entry]
    } else {
      this.stack = [...this.stack, entry]
    }
    this.chromeState.next(this.chromeOf(meta, snap))
  }

  private chromeOf(meta: NavMeta, snap: ActivatedRouteSnapshot): ChromeState {
    const isSchedule = meta.kind === 'screen' && meta.screen === 'schedule'
    const root = this.stack[0].meta
    return {
      back: isSchedule ? null : { label: this.stack.length > 1 ? this.stack[this.stack.length - 2].title : SCREENS[this.upOf(meta)].title },
      screen: root.kind === 'screen' ? root.screen : root.up,
      tip: tipFor(meta, snap),
      showNewWor: !meta.hideNewWor,
    }
  }

  private upOf(meta: NavMeta): ScreenKey {
    return meta.kind === 'screen' ? 'schedule' : meta.up
  }

  /** forward pushes, root resets the stack, replace swaps the top. Closes the drawer. */
  async go(t: NavTarget): Promise<void> {
    this.toggleDrawer(false)
    const tree = this.router.parseUrl(t.url)
    if (t.query) tree.queryParams = { ...t.query }
    if (t.mode === 'root') await this.nav.navigateRoot(tree)
    else await this.nav.navigateForward(tree, t.mode === 'replace' ? { replaceUrl: true } : undefined)
  }

  /** Depth > 1: browser history back, so header Back and hardware/browser back are one path.
   *  Depth 1 (a screen or a cold deep link): replace with the route's `up`. Schedule: nothing. */
  async back(): Promise<void> {
    this.toggleDrawer(false)
    const top = this.stack[this.stack.length - 1]
    if (!top || (top.meta.kind === 'screen' && top.meta.screen === 'schedule')) return
    if (this.stack.length > 1) return this.nav.back()
    const upPath = top.meta.kind === 'detail' && top.meta.upPath ? top.meta.upPath : screenPath(this.upOf(top.meta))
    await this.nav.navigateBack(this.router.parseUrl(upPath), { replaceUrl: true })
  }

  /** The menu screen back() lands on, or null when it lands on a detail page. */
  backScreen(): ScreenKey | null {
    const top = this.stack[this.stack.length - 1]
    if (!top) return null
    if (this.stack.length === 1) return this.upOf(top.meta)
    const prev = this.stack[this.stack.length - 2].meta
    return prev.kind === 'screen' ? prev.screen : null
  }

  /** Header ↻ (blocking: "Please wait" until every slice settles, capped at 15 s) and pull-to-refresh (non-blocking).
   *  Asks the active page about unsaved changes first. */
  async refresh(opts?: { readonly blocking?: boolean }): Promise<void> {
    const page = this.outlet?.isActivated ? (this.outlet.component as RefreshAware) : null
    if (page?.hasUnsavedChanges?.()) {
      const discard = await this.ui.confirmPrompt(
        'Discard unsaved changes?',
        "Refreshing reloads this page and loses the changes you haven't saved.",
        'Refresh',
        'Keep Editing',
      )
      if (!discard) return
    }
    page?.onRefresh?.()
    if (!opts?.blocking) return this.offline.refresh()
    // Subscribed before the refresh starts so the true → false edge can't be missed.
    const settled = firstValueFrom(race(this.refreshing$.pipe(skipWhile(r => !r), filter(r => !r)), timer(REFRESH_CAP_MS)))
    await this.busyWhile(Promise.all([this.offline.refresh(), settled, firstValueFrom(timer(REFRESH_MIN_MS))]))
  }

  /** Auto-hides after 3.5 s unless sticky. */
  toast(t: ToastSpec): void {
    if (this.toastTimer) clearTimeout(this.toastTimer)
    this.toastTimer = t.sticky ? null : setTimeout(() => this.dismissToast(), TOAST_MS)
    this.toastState.next(t)
  }

  dismissToast(): void {
    if (this.toastTimer) clearTimeout(this.toastTimer)
    this.toastTimer = null
    this.toastState.next(null)
  }

  async busyWhile<T>(work: Promise<T>, message?: string): Promise<T> {
    this.busyCount++
    this.busyState.next({ message: message ?? null })
    try {
      return await work
    } finally {
      if (--this.busyCount === 0) this.busyState.next(null)
    }
  }

  toggleDrawer(open?: boolean): void {
    this.drawerState.next(open ?? !this.drawerState.value)
  }

  toggleTip(): void {
    this.tipState.next(!this.tipState.value)
  }

  /** @internal ShellPage only: lets refresh() find the active page. */
  attachOutlet(outlet: IonRouterOutlet): void {
    this.outlet = outlet
  }
}
